import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Alert
} from '@mui/material';
import Button from 'components/generals/Button';
import { Videocam, Mic, VideocamOff, MicOff } from '@mui/icons-material';
import { t } from 'i18next';

interface MediaPermissionsModalProps {
    open: boolean;
    onAccept: (videoEnabled: boolean, audioEnabled: boolean) => void;
    onCancel: () => void;
}

/**
 * Modal para solicitar permisos de cámara y micrófono antes de unirse a videochat
 */
const MediaPermissionsModal: React.FC<MediaPermissionsModalProps> = ({
    open,
    onAccept,
    onCancel
}) => {
    const [videoEnabled, setVideoEnabled] = useState(true);
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [cameraBlocked, setCameraBlocked] = useState(false);
    const [micBlocked, setMicBlocked] = useState(false);

    // Check current permission status when modal opens
    useEffect(() => {
        if (!open) return;

        const checkPermissions = async () => {
            try {
                if (navigator.permissions) {
                    // Check camera permission
                    const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
                    setCameraBlocked(cameraPermission.state === 'denied');

                    // Check microphone permission
                    const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                    setMicBlocked(micPermission.state === 'denied');

                    console.log('[PERMISSIONS CHECK] Camera:', cameraPermission.state, 'Microphone:', micPermission.state);
                }
            } catch (err) {
                console.log('[PERMISSIONS CHECK] Unable to check permissions API:', err);
                // If Permissions API not available, we'll try getUserMedia and see what happens
            }
        };

        checkPermissions();
    }, [open]);

    const handleJoinCall = () => {
        // Simply pass the user's choices to the parent
        // Azure Communication Services will handle requesting permissions when needed
        onAccept(videoEnabled, audioEnabled);
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h6" component="div">
                    {t('videochat.mediaPermissions')}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('videochat.permissionsDescription')}
                </Typography>

                {/* Warning when permissions are blocked */}
                {(cameraBlocked || micBlocked) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('videochat.permissionsBlocked')}
                        </Typography>
                        <Typography variant="body2">
                            {cameraBlocked && micBlocked ?
                                t('videochat.bothPermissionsBlocked') :
                                cameraBlocked ? t('videochat.cameraBlocked') : t('videochat.micBlocked')
                            }
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                            {t('videochat.permissionsInstructions')}
                        </Typography>
                    </Alert>
                )}

                {/* Video Permission */}
                <Box sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        {videoEnabled ? <Videocam color="primary" /> : <VideocamOff color="disabled" />}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">
                                {t('videochat.camera')}
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={videoEnabled}
                                    onChange={(e) => setVideoEnabled(e.target.checked)}
                                    disabled={cameraBlocked}
                                />
                            }
                            label=""
                        />
                    </Box>
                </Box>

                {/* Audio Permission */}
                <Box sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        {audioEnabled ? <Mic color="primary" /> : <MicOff color="disabled" />}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">
                                {t('videochat.microphone')}
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={audioEnabled}
                                    onChange={(e) => setAudioEnabled(e.target.checked)}
                                    disabled={micBlocked}
                                />
                            }
                            label=""
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onCancel}
                    variant="text"
                    color="secondary"
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleJoinCall}
                    variant="contained"
                    color="primary"
                    disabled={!videoEnabled && !audioEnabled}
                >
                    {t('videochat.joinCall')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MediaPermissionsModal;
